import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import Authorities from "../../../../auth/authorities";
import withAuthority from "../../../../components/Auth/withAuthority";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { createIntegrationConfig } from "../../../../services/locationApp/integrationsService";
import {
  clearHubriseOAuthState,
  readHubriseOAuthState,
} from "../Integrations/hubriseOAuthState";

export interface IntegrationRedirectProps {}

/* HubRise sends the user back here after the OAuth screen. This page checks the
   OAuth state, reads the code from the query string, sends it to the backend so
   the integration can be stored against the location from the state, and then
   returns the user to that location's integrations list. */
const IntegrationRedirect: React.FunctionComponent<
  IntegrationRedirectProps
> = () => {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  // Where "Back to Integrations" goes: the location list until the state has
  // been checked.
  const [backPath, setBackPath] = useState("/");

  const integrationsPathFor = (locationId: string) =>
    `/${locationId}/presto-locations/integrations`;

  const saveHubriseIntegration = async () => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    // The location comes from the OAuth state, never from the url, and only
    // when the random value in it matches the one stored when the connect
    // started.
    const locationId = readHubriseOAuthState(params.get("state"));
    if (!locationId) {
      clearHubriseOAuthState();
      setError(
        "This HubRise connection could not be verified. Please start it again from the Integrations page.",
      );
      setIsLoading(false);
      return;
    }
    setBackPath(integrationsPathFor(locationId));
    if (!code) {
      clearHubriseOAuthState();
      setError("HubRise did not send an authorization code.");
      setIsLoading(false);
      return;
    }
    // The backend expects the HubRise authorization code as `auth_code`.
    const data: any = { auth_code: code };
    try {
      const response = await createIntegrationConfig(locationId, data);
      // The api can answer with http 200 while the body carries its own error
      // status (for example { status: 404, message: "No integration record is
      // available in Integration" }), so both statuses are checked.
      const bodyStatus = response?.data?.status;
      const isSaved =
        (response.status === 200 || response.status === 201) &&
        (!bodyStatus || bodyStatus === 200 || bodyStatus === 201);

      clearHubriseOAuthState();
      if (isSaved) {
        // Saved — send the user back to the integrations list. A full page
        // load (replace) so the location's details are loaded again and the
        // browser back button does not return to this callback url.
        window.location.replace(integrationsPathFor(locationId));
        return;
      }
      setError(response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoading(false);
    } catch (err) {
      clearHubriseOAuthState();
      setError(
        (err as any)?.response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR,
      );
      setIsLoading(false);
    }
  };

  useEffect(() => {
    saveHubriseIntegration();
  }, []);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "100vh", gap: "16px" }}
      >
        {isLoading ? (
          <>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Connecting HubRise, please wait…
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary">
              We could not complete the HubRise connection.
            </Typography>
            <ButtonCommon
              onClick={() => window.location.replace(backPath)}
              variant="contained"
              style={{ fontSize: 11, width: "180px" }}
              color={buttonColors.CREATE_BUTTON_COLOR}
            >
              {"Back to Integrations"}
            </ButtonCommon>
          </>
        )}
      </Box>
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

// Saving an integration is a write, so this needs the update authority.
export default withAuthority(IntegrationRedirect, Authorities.BASIC_INFO_UPDATE);